import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Clock, MailCheck, ShieldAlert } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';

const Shell = ({ icon: Icon, title, children, testId }) => (
  <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid={testId}>
    <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 space-y-4 text-center">
      <Icon className="w-10 h-10 text-primary mx-auto" /><h1 className="text-xl font-bold">{title}</h1>{children}
    </div>
  </div>
);

export function VerifySignup() {
  const [params] = useSearchParams();
  const [state, setState] = useState('working');
  const [msg, setMsg] = useState('');
  const once = useRef(false);
  useEffect(() => {
    if (once.current) return;  // StrictMode double-invoke guard – the token is single-use
    once.current = true;
    api.post('/api/auth/verify-signup', { token: params.get('token') }).then((r) => { setState('ok'); setMsg(r.data.message); })
      .catch((e) => { setState('bad'); setMsg(e.response?.data?.detail || 'This link is invalid or has expired'); });
  }, [params]);
  return (
    <Shell icon={MailCheck} title={state === 'ok' ? 'Email confirmed' : state === 'bad' ? 'Link not valid' : 'Confirming…'} testId="verify-signup-page">
      <p className="text-sm text-muted-foreground" data-testid="verify-signup-message">{msg}</p>
      {state !== 'working' && <Button asChild data-testid="verify-signup-login"><Link to="/login">Go to sign in</Link></Button>}
    </Shell>
  );
}

export function CheckEmail({ email }) {
  const [sent, setSent] = useState(false);
  return (
    <Shell icon={MailCheck} title="Check your email" testId="register-check-email">
      <p className="text-sm text-muted-foreground">We've sent a confirmation link to <strong>{email}</strong>. Open it to confirm your address – then a platform administrator reviews your practice before you can start.</p>
      <Button variant="outline" disabled={sent} onClick={() => api.post('/api/auth/resend-verification', { email }).then(() => setSent(true))} data-testid="register-resend-verification">{sent ? 'Sent again' : 'Resend the link'}</Button>
    </Shell>
  );
}

export default function PracticePending() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get('/api/practice/status').then((r) => setS(r.data)).catch(() => setS({ status: 'unknown' })); }, []);
  const out = () => api.post('/api/auth/logout').finally(() => { sessionStorage.clear(); window.location.href = '/login'; });
  if (s?.status === 'approved') { window.location.href = '/dashboard'; return null; }
  return (
    <Shell icon={s?.status === 'rejected' ? ShieldAlert : Clock} title={s?.status === 'rejected' ? 'Practice not approved' : 'Your practice is awaiting approval'} testId="practice-pending-page">
      <p className="text-sm text-muted-foreground" data-testid="practice-pending-message">
        {s?.status === 'rejected' ? `${s.name} wasn't approved${s.reason ? `: ${s.reason}` : '.'} Contact platform support if you think this is a mistake.`
          : `Thanks for signing up${s?.name ? ` ${s.name}` : ''}. A platform administrator reviews every new practice – we'll email you as soon as it's approved. Until then clients, invitations and sending are switched off.`}
      </p>
      <Button variant="outline" onClick={out} data-testid="practice-pending-logout">Sign out</Button>
    </Shell>
  );
}
